import { useMemo, useRef, useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { FinanceContactsSelectorDocument } from 'generated/resources/graphql'
import { compact } from 'lodash'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import LabeledInput from 'modules/core/components/LabeledInput'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivities } from 'modules/core/lib/activities/provider'
import i18n from 'modules/core/lib/i18n'

import { contactFullName } from 'modules/finance/lib/contactsUtils'

import {
  mapFinanceContacts,
  mapSelectedFinanceContacts,
  mapToFinanceContactFragment,
} from './mappers'
import {
  FinanceContactSelectorMultiple,
  FinanceContactSelectorSingle,
  FinanceContactSelectorSingleWithoutCreate,
  FinanceContactsSelectorProps,
  GroupType,
} from './types'

const isFinanceContactsSelectorSingleProps = (
  props: FinanceContactsSelectorProps
): props is FinanceContactSelectorSingle | FinanceContactSelectorSingleWithoutCreate => {
  return !props.multiple
}

const isFinanceContactsSelectorCreationProps = (
  props: FinanceContactsSelectorProps
): props is FinanceContactSelectorSingle | FinanceContactSelectorMultiple => {
  return !props.disableCreate
}

export const FinanceContactsSelector = (props: FinanceContactsSelectorProps) => {
  const {
    multiple,
    disabled,
    value,
    disableCreate,
    refetchQuery,
    queryParams,
    alwaysOpen,
    defaultOpen,
    width,
    minWidth,
    error,
    mandatory,
    label,
    emptyLabel,
    hideSearchIcon,
    validateOnCreate,
    variant = 'f0',
  } = props

  const { openActivity } = useActivities()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const openCreateFinanceContactActivity = (search?: string) => {
    if (isFinanceContactsSelectorCreationProps(props)) {
      openActivity('finance.contactCreate', {
        refetchQuery: refetchQuery,
        name: search,
        contactCreateType: props.queryParams.contactCreateType,
        validateOnCreate: validateOnCreate ? 'validateOnCreate' : undefined,
      })
    }
  }
  const [skip, setSkip] = useState<boolean>(!(alwaysOpen || defaultOpen))

  const { loading, data } = useQuery(FinanceContactsSelectorDocument, {
    variables: {
      companyId,
      contactType: queryParams.contactSearchType,
      search: queryParams.search,
    },
    skip,
  })

  const financeContacts = data?.finance.contacts

  const canCreateByPolicy = data?.finance._policies.contactsCreateOrUpdate.can

  const onOpen = () => {
    if (skip) setSkip(false)
  }

  const financeContactsEntities = useMemo(
    () => mapFinanceContacts(financeContacts ?? []),
    [financeContacts]
  )

  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)
  const previousContactsRef = useRef<EntitySelectEntity[]>([])

  const selectNewContact = useUnsafeCallback(
    (contact: EntitySelectEntity) => {
      if (isFinanceContactsSelectorSingleProps(props)) {
        props.onSelect(mapToFinanceContactFragment(contact, financeContacts ?? []))
      } else {
        const currentSelection = Array.isArray(value) ? value : []
        props.onSelect([
          ...currentSelection,
          mapToFinanceContactFragment(contact, financeContacts ?? []),
        ])
      }
    },
    [props, value, financeContacts]
  )

  useUnsafeEffect(() => {
    if (!isInitialLoadComplete && !loading && financeContactsEntities.length > 0) {
      setIsInitialLoadComplete(true)
      previousContactsRef.current = financeContactsEntities
    }
  }, [loading, financeContactsEntities, isInitialLoadComplete])

  useEffect(() => {
    return () => {
      setIsInitialLoadComplete(false)
      previousContactsRef.current = []
    }
  }, [skip])

  useUnsafeEffect(() => {
    if (!isInitialLoadComplete) return

    const hasNewContacts = financeContactsEntities.length > previousContactsRef.current.length
    if (!hasNewContacts) {
      previousContactsRef.current = financeContactsEntities
      return
    }

    const newContact = financeContactsEntities.find(
      (contact) => !previousContactsRef.current.some((prev) => prev.id === contact.id)
    )

    if (newContact) {
      selectNewContact(newContact)
    }

    previousContactsRef.current = financeContactsEntities
  }, [financeContactsEntities, isInitialLoadComplete, selectNewContact])

  const selectedFinanceContacts = useMemo(
    () =>
      multiple
        ? mapSelectedFinanceContacts(value, financeContactsEntities)
        : value
          ? [
              {
                id: value.id,
                name: value ? contactFullName(value) : '',
              },
            ]
          : [],
    [multiple, value, financeContactsEntities]
  )

  const selectedFinanceContactsForInput = useMemo(() => {
    const contacts = Array.isArray(value) ? value : compact([value])

    return contacts.map((contact) => ({
      id: contact.id,
      name: value ? contactFullName(contact) : '',
    }))
  }, [value])

  const groupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: financeContactsEntities,
    }),
    [financeContactsEntities]
  )

  const selectedGroupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: selectedFinanceContacts ?? [],
    }),
    [selectedFinanceContacts]
  )

  const handleOnSelectMultiple = (selection: EntitySelectEntity[] | null) => {
    if (!isFinanceContactsSelectorSingleProps(props) && !!financeContacts) {
      props.onSelect(
        selection ? selection.map((s) => mapToFinanceContactFragment(s, financeContacts)) : []
      )
    }
  }

  const handleOnSelectSingle = (selection: EntitySelectEntity | null) => {
    if (isFinanceContactsSelectorSingleProps(props) && !!financeContacts) {
      props.onSelect(selection ? mapToFinanceContactFragment(selection, financeContacts) : null)
    }
  }

  const firstFinanceContact = selectedFinanceContacts[0]
  const name = !!value && firstFinanceContact ? firstFinanceContact.name : undefined

  return (
    <Box minWidth={minWidth} width={width ?? minWidth ?? 'full'} position="relative">
      <EntitySelectWrapper
        {...props}
        value={props.value}
        label={label ?? i18n.t('finance.contacts.selector.select_contacts')}
        hideLabel={variant === 'gamma'}
        hiddenAvatar
        size="md"
        loading={loading}
        onOpen={onOpen}
        entities={groupMap['all'] || []}
        selectedGroup={'all'}
        selectedGroupedEntities={selectedGroupMap['all']}
        onSelectExpandedElements={() => {}}
        expandedElements={[]}
        selectedEntities={selectedFinanceContacts}
        searchPlaceholder={i18n.t('finance.contacts.selector.search')}
        selectAllLabel={i18n.t('finance.contacts.selector.select_all')}
        clearLabel={i18n.t('finance.contacts.selector.clear')}
        selectedLabel={i18n.t('finance.contacts.selector.selected')}
        notFoundSubtitle={i18n.t('finance.contacts.selector.not_found.subtitle')}
        notFoundTitle={i18n.t('finance.contacts.selector.not_found.title')}
        placeholder={i18n.t('finance.contacts.selector.select_contacts')}
        selectedItemsCopy={i18n.t('finance.contacts.selector.contacts_selected')}
        groups={[{ label: i18n.t('finance.contacts.selector.groups.none'), value: 'all' }]}
        onGroupChange={() => {}}
        onSelectMultiple={handleOnSelectMultiple}
        singleSelector={!multiple}
        onSelect={handleOnSelectSingle}
        minWidth={minWidth}
        width={width}
        onCreate={
          !disableCreate && canCreateByPolicy ? openCreateFinanceContactActivity : undefined
        }
        onCreateLabel={i18n.t('finance.contacts.selector.create_new_contact')}
      >
        {variant === 'gamma' && (
          <Box flexDirection="row" width="full">
            <LabeledInput
              aria-label="serial-sequence-selector"
              readonly={disabled}
              error={error}
              mandatory={mandatory}
              label={label}
              value={value?.toString() ?? undefined}
            >
              <Box
                paddingX="s16"
                paddingY="s8"
                flexDirection="row"
                height="s40"
                alignItems="center"
                width="full"
                cursor={disabled ? 'default' : 'pointer'}
              >
                <Box flexDirection="row" width="full" gap="s6">
                  {!hideSearchIcon && (
                    <Icon.Medium icon="search" color={disabled ? 'grey600' : 'grey800'} />
                  )}
                  <Box overflowX="hidden" textAlign="left">
                    {multiple ? (
                      <Text.Normal ellipsis>
                        {selectedFinanceContactsForInput &&
                        selectedFinanceContactsForInput.length > 0
                          ? selectedFinanceContactsForInput.map((el) => el.name).join(', ')
                          : (props.emptyLabel ?? i18n.t('finance.contacts.selector.empty_label'))}
                      </Text.Normal>
                    ) : (
                      <Text.Normal ellipsis>
                        {name ?? emptyLabel ?? i18n.t('finance.contacts.selector.empty_label')}
                      </Text.Normal>
                    )}
                  </Box>
                </Box>
                <Icon.Small icon="caret-down" />
              </Box>
            </LabeledInput>
          </Box>
        )}
      </EntitySelectWrapper>
    </Box>
  )
}
